import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import {
  Text,
  Margin,
  Form,
  Row,
  FormInput,
  FormButton,
} from 'shared/components'
import axios from 'axios'
import { ChooseWinner } from './components/chooseWinner'
import * as styles from './styles'

const updateAsyncFlag = (setter, triggerId, value) => {
  setter((prev) => ({
    ...prev,
    [triggerId]: value,
  }))
}

const markTriggerResolved = (setTriggers, triggerId) => {
  setTriggers((prev) =>
    prev.map((trigger) =>
      trigger.id === triggerId
        ? {
            ...trigger,
            status: 'RESOLVED',
          }
        : trigger
    )
  )
}

const scheduleTriggerRefresh = (setRefreshTriggers) => {
  setTimeout(() => {
    setRefreshTriggers((prev) => prev + 1)
  }, 1000)
}

const finalizeTriggerAction = (setter, triggerId, delay) => {
  setTimeout(() => {
    updateAsyncFlag(setter, triggerId, false)
  }, delay)
}

const isCustomSportType = (sportType) =>
  sportType === 'athletics' || sportType === 'other' || sportType === 'OTHER'

const hasNoTeams = (event) => {
  const hasEmptyTeamNames =
    (!event.teamA || event.teamA === '') && (!event.teamB || event.teamB === '')
  const hasNoTeamObjects = !event.teamOne && !event.teamTwo

  return hasEmptyTeamNames || hasNoTeamObjects
}

const detectCustomEvent = (event, isCustomEvent) => {
  if (typeof isCustomEvent === 'boolean') {
    return isCustomEvent
  }

  if (isCustomSportType(event.sportType)) {
    return true
  }

  return hasNoTeams(event)
}

const getResolveButtonLabel = ({ resolvingEvent, eventResolveSuccess }) => {
  if (resolvingEvent) {
    return 'Resolving...'
  }

  if (eventResolveSuccess) {
    return 'Resolved!'
  }

  return 'Resolve'
}

const customEventContainerStyle = {
  textAlign: 'left',
  marginBottom: '20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}

const eventNameStyle = {
  fontSize: '14px',
  fontWeight: 'normal',
  fontFamily: 'inherit',
  lineHeight: '1.4',
  color: 'white',
  marginRight: '10px',
}

const triggersLabelStyle = {
  fontSize: '18px',
  fontWeight: 'bold',
  textAlign: 'left',
  marginTop: '15px',
  marginBottom: '5px',
  color: 'white',
}

const triggersListStyle = {
  textAlign: 'left',
  color: 'white',
  fontSize: '14px',
  marginBottom: '20px',
  listStyle: 'none',
  padding: '0',
}

const triggerItemStyle = {
  padding: '6px 0',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}

const triggerTextStyle = {
  flex: 1,
  paddingRight: '10px',
}

const loadingStyle = {
  color: 'rgba(255, 255, 255, 0.7)',
  fontStyle: 'italic',
  fontSize: '14px',
}

const errorStyle = {
  color: '#ff6b6b',
  fontSize: '14px',
}

const resolvedLabelStyle = {
  color: '#78bd7d',
  fontSize: '11px',
  fontWeight: 'bold',
}

const successMessageStyle = {
  color: '#78bd7d',
  fontSize: '12px',
  marginTop: '5px',
  marginBottom: '10px',
}

const preventFormSubmission = (e) => {
  if (!e) {
    return
  }

  e.preventDefault()
  e.stopPropagation()
}

const countResolvedTriggers = (triggers) =>
  triggers.filter((trigger) => trigger.status === 'RESOLVED').length

const resolveCustomEventRequest = async ({
  event,
  resolvingEvent,
  setResolvingEvent,
  setError,
  setEventResolveSuccess,
  closeModal,
  e,
}) => {
  preventFormSubmission(e)

  if (resolvingEvent || !event.id) {
    return
  }

  setResolvingEvent(true)
  setError(null)

  try {
    const endTime = new Date()
    endTime.setFullYear(endTime.getFullYear() + 1)

    await axios.post(`/admin/events/${event.id}/resolve`, {
      endTime: endTime.toISOString(),
    })

    setEventResolveSuccess(true)
    setResolvingEvent(false)
    setTimeout(closeModal, 1500)
  } catch (err) {
    console.error(`Error resolving event ${event.id}:`, err)
    setError(`Failed to resolve event: ${err.message}`)
    setTimeout(() => {
      setResolvingEvent(false)
    }, 3000)
  }
}

const submitTriggerDecision = async ({
  triggerId,
  e,
  activeRequests,
  setActiveRequests,
  setTriggers,
  setRefreshTriggers,
  resolution,
  errorLabel,
}) => {
  preventFormSubmission(e)

  if (activeRequests[triggerId]) {
    return
  }

  updateAsyncFlag(setActiveRequests, triggerId, true)

  try {
    await axios.post(`/admin/triggers/${triggerId}/resolve/${resolution}`)
    markTriggerResolved(setTriggers, triggerId)
    finalizeTriggerAction(setActiveRequests, triggerId, 500)
    scheduleTriggerRefresh(setRefreshTriggers)
  } catch (err) {
    console.error(`Error ${errorLabel} trigger ${triggerId}:`, err)
    finalizeTriggerAction(setActiveRequests, triggerId, 1000)
  }
}

const formatTriggerDescription = (trigger) => {
  if (!trigger) return ''

  if (trigger.description) return trigger.description

  const parts = []

  if (trigger.category) parts.push(trigger.category)
  if (trigger.subCategory) parts.push(trigger.subCategory)
  if (trigger.event?.matchName) parts.push(trigger.event.matchName)

  return parts.join(' - ') || 'No description available'
}

const BlockInWinsField = ({ changeInwinnable, form }) => (
  <div css={styles.wrapperSwitcher}>
    <Text as="label" size={11} css={styles.wrapperLabel}>
      Block for InWins
    </Text>
    <FormInput
      oldInput
      name="isBlockInWins"
      type="toggle"
      onChange={({ target }) => {
        changeInwinnable(target.value)
        form.fields.isBlockInWins.onChange({ target })
      }}
    />
  </div>
)

BlockInWinsField.propTypes = {
  changeInwinnable: PropTypes.func.isRequired,
  form: PropTypes.shape({
    fields: PropTypes.shape({
      isBlockInWins: PropTypes.shape({
        onChange: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
  }).isRequired,
}

const TriggerActions = ({
  trigger,
  resolvingTriggers,
  rejectingTriggers,
  resolveTrigger,
  rejectTrigger,
}) => {
  if (trigger.status === 'RESOLVED') {
    return <span style={resolvedLabelStyle}>Resolved</span>
  }

  return (
    <div style={{ display: 'flex', gap: '8px' }}>
      <FormButton
        small
        primary
        disabled={
          resolvingTriggers[trigger.id] || rejectingTriggers[trigger.id]
        }
        onClick={(e) => resolveTrigger(trigger.id, e)}
        type="button"
      >
        <Text size={11}>
          {resolvingTriggers[trigger.id] ? 'Resolving...' : 'True'}
        </Text>
      </FormButton>
      <FormButton
        small
        secondary
        disabled={
          resolvingTriggers[trigger.id] || rejectingTriggers[trigger.id]
        }
        onClick={(e) => rejectTrigger(trigger.id, e)}
        type="button"
        css={{
          borderColor: '#ff6b6b',
          color: '#ff6b6b',
          '&:hover': {
            borderColor: '#ff4f4f',
            color: '#ff4f4f',
          },
        }}
      >
        <Text size={11} css={{ color: '#ff6b6b' }}>
          {rejectingTriggers[trigger.id] ? 'Rejecting...' : 'False'}
        </Text>
      </FormButton>
    </div>
  )
}

TriggerActions.propTypes = {
  trigger: PropTypes.shape({
    id: PropTypes.string.isRequired,
    status: PropTypes.string,
  }).isRequired,
  resolvingTriggers: PropTypes.objectOf(PropTypes.bool).isRequired,
  rejectingTriggers: PropTypes.objectOf(PropTypes.bool).isRequired,
  resolveTrigger: PropTypes.func.isRequired,
  rejectTrigger: PropTypes.func.isRequired,
}

const CustomEventSection = ({
  event,
  resolveCustomEvent,
  resolvingEvent,
  eventResolveSuccess,
  error,
  loading,
  triggers,
  resolvingTriggers,
  rejectingTriggers,
  resolveTrigger,
  rejectTrigger,
  resolvedTriggerCount,
  changeInwinnable,
  form,
}) => {
  const resolveButtonLabel = getResolveButtonLabel({
    resolvingEvent,
    eventResolveSuccess,
  })
  let triggersContent

  if (loading) {
    triggersContent = <div style={loadingStyle}>Loading triggers...</div>
  } else if (error) {
    triggersContent = <div style={errorStyle}>{error}</div>
  } else if (triggers.length === 0) {
    triggersContent = <div style={loadingStyle}>No triggers found for this event</div>
  } else {
    triggersContent = (
      <ul style={triggersListStyle}>
        {triggers.map((trigger) => (
          <li key={trigger.id} style={triggerItemStyle}>
            <div style={triggerTextStyle}>
              {formatTriggerDescription(trigger)}
            </div>
            <TriggerActions
              trigger={trigger}
              resolvingTriggers={resolvingTriggers}
              rejectingTriggers={rejectingTriggers}
              resolveTrigger={resolveTrigger}
              rejectTrigger={rejectTrigger}
            />
          </li>
        ))}
      </ul>
    )
  }

  return (
    <React.Fragment>
      <div style={customEventContainerStyle}>
        <span style={eventNameStyle}>{event.matchName || 'Event'}</span>

        <FormButton
          small
          primary
          disabled={resolvingEvent || eventResolveSuccess}
          type="button"
          onClick={resolveCustomEvent}
        >
          <Text size={11}>{resolveButtonLabel}</Text>
        </FormButton>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {eventResolveSuccess && (
        <div style={successMessageStyle}>Event successfully resolved!</div>
      )}

      <div style={triggersLabelStyle}>Triggers:</div>

      {triggersContent}

      {resolvedTriggerCount > 0 && (
        <div style={successMessageStyle}>
          Successfully resolved {resolvedTriggerCount} trigger
          {resolvedTriggerCount > 1 ? 's' : ''}.
        </div>
      )}

      <input type="hidden" name="binaryWinner" value="CUSTOM_EVENT" />

      <input type="hidden" name="skipApiCall" value="true" />

      <Row css={styles.submitWrapper}>
        <BlockInWinsField changeInwinnable={changeInwinnable} form={form} />
      </Row>
    </React.Fragment>
  )
}

CustomEventSection.propTypes = {
  event: PropTypes.shape({
    matchName: PropTypes.string,
  }).isRequired,
  resolveCustomEvent: PropTypes.func.isRequired,
  resolvingEvent: PropTypes.bool.isRequired,
  eventResolveSuccess: PropTypes.bool.isRequired,
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  triggers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ).isRequired,
  resolvingTriggers: PropTypes.objectOf(PropTypes.bool).isRequired,
  rejectingTriggers: PropTypes.objectOf(PropTypes.bool).isRequired,
  resolveTrigger: PropTypes.func.isRequired,
  rejectTrigger: PropTypes.func.isRequired,
  resolvedTriggerCount: PropTypes.number.isRequired,
  changeInwinnable: PropTypes.func.isRequired,
  form: PropTypes.shape().isRequired,
}

const TeamSelectionSection = ({ options, changeInwinnable, form }) => (
  <React.Fragment>
    <Margin bottom={2}>
      <FormInput
        fluid
        oldInput
        name="binaryWinner"
        renderComponent={ChooseWinner}
        options={options}
      />
    </Margin>

    <FormInput type="hidden" name="eventId" />

    <Row css={styles.submitWrapper}>
      <BlockInWinsField changeInwinnable={changeInwinnable} form={form} />
      <div css={styles.wrapperButton}>
        <FormButton submit long small primary>
          <Text size={11}>Resolve</Text>
        </FormButton>
      </div>
    </Row>
  </React.Fragment>
)

TeamSelectionSection.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      value: PropTypes.string,
      logo: PropTypes.string,
    })
  ),
  changeInwinnable: PropTypes.func.isRequired,
  form: PropTypes.shape().isRequired,
}

const BinaryBetFormComponent = ({
  initialValues: event,
  form,
  onSubmit,
  changeInwinnable,
  isCustomEvent,
  onClose, // Add onClose prop to close modal directly
}) => {
  // State for triggers
  const [triggers, setTriggers] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [resolvingTriggers, setResolvingTriggers] = useState({})
  const [rejectingTriggers, setRejectingTriggers] = useState({})
  const [resolvingEvent, setResolvingEvent] = useState(false)
  const [refreshTriggers, setRefreshTriggers] = useState(0) // Counter to trigger refresh
  const [eventResolveSuccess, setEventResolveSuccess] = useState(false)

  // Determine if this is a custom event without teams
  const isCustomEventWithoutTeams = detectCustomEvent(event, isCustomEvent)

  // Create appropriate options based on whether teams exist
  const options = !isCustomEventWithoutTeams
    ? [
        { id: 'TEAM_A', value: event.teamA, logo: event.teamOne?.logo },
        { id: 'TEAM_B', value: event.teamB, logo: event.teamTwo?.logo },
      ]
    : null // Don't create team options for custom events

  // Reset success state when event changes
  useEffect(() => {
    if (event?.id) {
      setEventResolveSuccess(false)
      setResolvingEvent(false)
      setError(null)

      // Check if event is already resolved
      if (event.resolved === true) {
        setEventResolveSuccess(true)
      }
    }
  }, [event?.id])

  // Fetch triggers when component mounts or event changes or refreshTriggers changes
  useEffect(() => {
    const fetchTriggers = async () => {
      if (!event?.id) return

      setLoading(true)
      setError(null)

      try {
        // Fetch triggers with a cache-busting parameter to ensure fresh data
        const response = await axios.get(
          `/admin/triggers/event/${event.id}?_=${Date.now()}`
        )

        // The response is now a direct array of trigger objects, not wrapped in a content object
        const triggersData = Array.isArray(response.data) ? response.data : []
        setTriggers(triggersData)
      } catch (err) {
        console.error('Error fetching triggers:', err)
        setError('Failed to load triggers')
      } finally {
        setLoading(false)
      }
    }

    fetchTriggers()
  }, [event, refreshTriggers])

  const closeModal = () => {
    if (onClose && typeof onClose === 'function') {
      onClose()
    }
  }

  const resolveCustomEvent = async (e) => {
    await resolveCustomEventRequest({
      event,
      resolvingEvent,
      setResolvingEvent,
      setError,
      setEventResolveSuccess,
      closeModal,
      e,
    })
  }

  const handleFormSubmit = (formData, formObject) => {
    if (isCustomEventWithoutTeams) {
      return false
    }

    if (onSubmit) {
      onSubmit(formData, formObject)
    }
  }

  const resolveTrigger = async (triggerId, e) => {
    await submitTriggerDecision({
      triggerId,
      e,
      activeRequests: resolvingTriggers,
      setActiveRequests: setResolvingTriggers,
      setTriggers,
      setRefreshTriggers,
      resolution: true,
      errorLabel: 'resolving',
    })
  }

  const rejectTrigger = async (triggerId, e) => {
    await submitTriggerDecision({
      triggerId,
      e,
      activeRequests: rejectingTriggers,
      setActiveRequests: setRejectingTriggers,
      setTriggers,
      setRefreshTriggers,
      resolution: false,
      errorLabel: 'rejecting',
    })
  }

  const resolvedTriggerCount = countResolvedTriggers(triggers)

  return (
    <Form form={form} onSubmit={handleFormSubmit} css={styles.form}>
      {isCustomEventWithoutTeams ? (
        <CustomEventSection
          event={event}
          resolveCustomEvent={resolveCustomEvent}
          resolvingEvent={resolvingEvent}
          eventResolveSuccess={eventResolveSuccess}
          error={error}
          loading={loading}
          triggers={triggers}
          resolvingTriggers={resolvingTriggers}
          rejectingTriggers={rejectingTriggers}
          resolveTrigger={resolveTrigger}
          rejectTrigger={rejectTrigger}
          resolvedTriggerCount={resolvedTriggerCount}
          changeInwinnable={changeInwinnable}
          form={form}
        />
      ) : (
        <TeamSelectionSection
          options={options}
          changeInwinnable={changeInwinnable}
          form={form}
        />
      )}

      <FormInput type="hidden" name="eventId" />
    </Form>
  )
}

BinaryBetFormComponent.propTypes = {
  initialValues: PropTypes.shape(),
  form: PropTypes.shape(),
  onSubmit: PropTypes.func.isRequired,
  changeInwinnable: PropTypes.func.isRequired,
  isCustomEvent: PropTypes.bool,
  onClose: PropTypes.func,
}

export default BinaryBetFormComponent
